/**
 * Zustand store for import wizard state
 */

import { create } from 'zustand'
import type { FileUploadResponse, ImportResult, EditionMode } from '../types'

export type ImportStep = 'upload' | 'preview' | 'configure' | 'progress' | 'complete'

interface ImportState {
  // Current step
  step: ImportStep

  // Uploaded files
  adFile: File | null
  nadFile: File | null

  // Upload response
  uploadResponse: FileUploadResponse | null

  // Import result
  importResult: ImportResult | null

  // Import mode
  importMode: 'upsert' | 'replace'

  // Edition settings
  editionMode: EditionMode
  editionName: string
  targetEditionId: number | null

  // Loading states
  isUploading: boolean
  isImporting: boolean

  // Error
  error: string | null

  // Actions
  setStep: (step: ImportStep) => void
  setFiles: (adFile: File | null, nadFile: File | null) => void
  setUploadResponse: (response: FileUploadResponse | null) => void
  setImportResult: (result: ImportResult | null) => void
  setImportMode: (mode: 'upsert' | 'replace') => void
  setEditionMode: (mode: EditionMode) => void
  setEditionName: (name: string) => void
  setTargetEditionId: (id: number | null) => void
  setUploading: (isUploading: boolean) => void
  setImporting: (isImporting: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
}

const initialState = {
  step: 'upload' as ImportStep,
  adFile: null,
  nadFile: null,
  uploadResponse: null,
  importResult: null,
  importMode: 'upsert' as const,
  editionMode: 'update_existing' as EditionMode,
  editionName: '',
  targetEditionId: null,
  isUploading: false,
  isImporting: false,
  error: null,
}

export const useImportStore = create<ImportState>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),
  setFiles: (adFile, nadFile) => set({ adFile, nadFile }),
  setUploadResponse: (uploadResponse) => set({ uploadResponse }),
  setImportResult: (importResult) => set({ importResult }),
  setImportMode: (importMode) => set({ importMode }),
  setEditionMode: (editionMode) => set({ editionMode }),
  setEditionName: (editionName) => set({ editionName }),
  setTargetEditionId: (targetEditionId) => set({ targetEditionId }),
  setUploading: (isUploading) => set({ isUploading }),
  setImporting: (isImporting) => set({ isImporting }),
  setError: (error) => set({ error }),
  reset: () => set(initialState),
}))
